import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Paper,
} from "@mui/material";
import { CheckCircle, AccessTime, Cancel } from "@mui/icons-material";
import api from "../../api/axios";
import socket from "../../socket";
import { useSnackbar } from "../../utils/SnackbarProvider";

const VendorOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const vendor = JSON.parse(localStorage.getItem("user"));
  const { showSnackbar } = useSnackbar();
  // ⛔ Protect vendor route
  useEffect(() => {
    if (!vendor?._id) {
      navigate("/vendor/login", { replace: true });
    }
  }, [vendor, navigate]);

  // 📦 Fetch order
  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/vendor/orders/${orderId}`, {
        params: { vendorId: vendor._id },
      });
      setOrder(data);

      /* Join vendor room for real-time updates */
      socket.emit("vendor_join", vendor._id);
    } catch (error) {
      console.error("Failed to load order:", error);
      navigate("/vendor/orders", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [orderId, vendor?._id, navigate]);

  useEffect(() => {
    if (vendor?._id && orderId) {
      fetchOrder();
    }
  }, [fetchOrder]);

  // 📡 Listen for real-time updates
  useEffect(() => {
    socket.emit("join_order", orderId);
    console.log("✓ Vendor joined order room:", orderId);

    // Vendor phase updates
    socket.on("vendor_order_updated", (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({
          ...prev,
          vendorPhase: data.vendorPhase,
          agentPhase: data.agentPhase,
          deliveryPhase: data.deliveryPhase,
          tracking: data.tracking,
        }));
      }
    });

    // Agent accepted delivery
    socket.on("agent_accepted", (data) => {
      if (data.orderId === orderId) {
        console.log("🟠 Agent accepted delivery:", data);
        setOrder((prev) => ({
          ...prev,
          agentPhase: data.agentPhase,
          agent: data.agent,
          tracking: data.tracking,
          status: data.status,
        }));
      }
    });

    // Order status updates from agent actions
    socket.on("order_status_updated", (data) => {
      if (data.orderId === orderId) {
        console.log("📦 Order status updated:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
          deliveryPhase: data.deliveryPhase,
          agentPhase: data.agentPhase,
        }));
      }
    });

    // Agent picked up order
    socket.on("order_picked_up", (data) => {
      if (data.orderId === orderId) {
        console.log("📍 Agent picked up order:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
          deliveryPhase: { ...prev.deliveryPhase, status: "PICKED_UP" },
        }));
      }
    });

    // Agent in transit
    socket.on("order_in_transit", (data) => {
      if (data.orderId === orderId) {
        console.log("🚚 Order in transit:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
          deliveryPhase: { ...prev.deliveryPhase, status: "IN_TRANSIT" },
        }));
      }
    });

    // Order delivered
    socket.on("order_delivered", (data) => {
      if (data.orderId === orderId) {
        console.log("✅ Order delivered:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
          deliveryPhase: { ...prev.deliveryPhase, status: "DELIVERED" },
        }));
      }
    });

    // Preparation updates (images, notes)
    socket.on("preparation_update", (data) => {
      if (data.orderId === orderId) {
        console.log("📸 Preparation update received:", data);
        setOrder((prev) => ({
          ...prev,
          status: data.status || prev.status,
          tracking: data.tracking || prev.tracking,
          vendorPhase: data.vendorPhase || prev.vendorPhase,
          preparationUpdates: data.preparationUpdates || prev.preparationUpdates,
        }));
      }
    });

    return () => {
      socket.off("vendor_order_updated");
      socket.off("agent_accepted");
      socket.off("order_status_updated");
      socket.off("order_picked_up");
      socket.off("order_in_transit");
      socket.off("order_delivered");
      socket.off("preparation_update");
    };
  }, [orderId, fetchOrder]);

  // 🟣 PHASE 1: VENDOR ACTIONS
  const handleAccept = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "ACCEPT",
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
    } catch (err) {
      console.error("Failed to accept order:", err);
      showSnackbar(err.response?.data?.message || "Failed to accept order", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleStartPreparing = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "PREPARING",
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
    } catch (error) {
      console.error("Failed to start preparing order:", error);
       showSnackbar(error.response?.data?.message || "Failed to start preparing order");
    } finally {
      setLoading(false);
    }
  };

  // Unified cancel: PENDING uses REJECT, everything else uses CANCEL
  const handleCancel = async () => {
    try {
      setLoading(true);
      const currentPhase = order?.vendorPhase?.status;
      const vendorAction = currentPhase === "PENDING" ? "REJECT" : "CANCEL";

      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction,
        reason: cancelReason,
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
      setCancelDialog(false);
      setCancelReason("");
      showSnackbar("Order cancelled successfully", "success");
    } catch (error) {
      console.error("Failed to cancel order:", error);
      showSnackbar(error.response?.data?.message || "Failed to cancel order", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReadyForPickup = async () => {
    try {
      setLoading(true);
      await api.patch(`/vendor/orders/${orderId}/status`, {
        vendorAction: "READY_FOR_PICKUP",
      }, {
        params: { vendorId: vendor._id },
      });
      await fetchOrder();
    } catch (error) {
      console.error("Failed to mark ready:", error);
      showSnackbar(error.response?.data?.message || "Failed to mark items as ready", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!order) return null;

  const vendorPhase = order.vendorPhase || {};
  const agentPhase = order.agentPhase || {};
  const deliveryPhase = order.deliveryPhase || {};

  // Hide cancel if order has moved past pickup
  const lastTrackingKey = order.tracking?.at(-1)?.key;
  const hideCancel = ["PICKED_UP", "IN_TRANSIT", "DELIVERED"].includes(lastTrackingKey);

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4, backgroundColor: "#fff", p: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <Button
          onClick={() => navigate("/vendor/orders")}
          sx={{ mb: 2, textTransform: "none", color: "#007185", fontSize: 14, p: 0 }}
        >
          ← Back to Orders
        </Button>

        <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: "#0f1111" }}>
          Order #{orderId.substring(0, 8).toUpperCase()}
        </Typography>
        <Typography variant="body2" color="#565959">
          Manage order preparation and delivery
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left: Order Items */}
        <Grid item xs={12} md={8}>
          {/* Items */}
          <Card sx={{ mb: 3, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#0f1111" }}>
                📦 Order Items
              </Typography>
              {order.items?.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: 1,
                    mb: 1,
                    border: "1px solid #e7e7e7",
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#0f1111">
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="#565959" sx={{ mt: 0.5 }}>
                      Qty: {item.qty}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} color="#b12704">
                    ₹{item.price}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e7e7e7" }}>
                <Typography variant="body2" fontWeight={700} color="#0f1111">
                  Total: <span style={{ color: "#b12704", fontSize: "1.1em" }}>₹{order.total}</span>
                </Typography>
                <Typography variant="caption" color="#565959">
                  Payment: {order.paymentMethod?.toUpperCase()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card sx={{ mb: 3, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#0f1111" }}>
                📍 Delivery Address
              </Typography>
              <Paper sx={{ p: 2, backgroundColor: "#f9f9f9", border: "1px solid #e7e7e7", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, color: "#0f1111", fontWeight: 500 }}>
                  {order.address?.addressLine1}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: "#0f1111" }}>
                  {order.address?.city}, {order.address?.state} {order.address?.pincode}
                </Typography>
              </Paper>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card sx={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#0f1111" }}>
                📋 Order Timeline
              </Typography>
              <Stack spacing={1}>
                {order.tracking?.map((track, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: 2,
                      backgroundColor: track.completed ? "#f0fdf4" : "#fafafa",
                      borderRadius: 1,
                      borderLeft: track.completed
                        ? "3px solid #10b981"
                        : "3px solid #d1d5db",
                      mb: 1,
                    }}
                  >
                    <CheckCircle
                      sx={{
                        fontSize: 22,
                        color: track.completed ? "#10b981" : "#d1d5db",
                        mr: 2,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="#0f1111">
                        {track.label}
                      </Typography>
                      <Typography variant="caption" color="#565959" sx={{ mt: 0.5 }}>
                        {new Date(track.time).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Vendor Phase Controls */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: "#0f1111" }}>
                🟣 Your Responsibility
              </Typography>

              {/* Vendor Phase Status */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: "#f9f9f9", borderRadius: 1, border: "1px solid #e7e7e7" }}>
                <Typography variant="caption" color="#565959" fontWeight={600}>
                  Preparation Status
                </Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Chip
                    icon={<CheckCircle />}
                    label={(vendorPhase.status || "PENDING").replace(/_/g, " ")}
                    color={
                      vendorPhase.status === "ACCEPTED"
                        ? "success"
                        : vendorPhase.status === "REJECTED" || vendorPhase.status === "CANCELLED"
                        ? "error"
                        : "default"
                    }
                    sx={{ width: "100%", fontWeight: 600, fontSize: 12 }}
                  />
                </Box>
              </Box>

              {/* PHASE 1: PENDING - Show Accept + Cancel (only before pickup) */}
              {vendorPhase.status === "PENDING" && (
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleAccept}
                    disabled={loading}
                  >
                    ✓ Accept Order
                  </Button>
                  {!hideCancel && (
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={() => setCancelDialog(true)}
                      disabled={loading}
                      startIcon={<Cancel />}
                    >
                      ✗ Cancel Order
                    </Button>
                  )}
                </Stack>
              )}

              {/* PHASE 1: ACCEPTED - Show Start Preparing + Cancel */}
              {vendorPhase.status === "ACCEPTED" && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ✓ Order Accepted
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Click below to start preparing the order
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, backgroundColor: "#f0f7ff", borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Estimated Ready Time
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                      <AccessTime sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }} />
                      {vendorPhase.estimatedReadyTime
                        ? new Date(vendorPhase.estimatedReadyTime).toLocaleTimeString()
                        : "30 mins"}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleStartPreparing}
                    disabled={loading}
                  >
                    ▶ Start Preparing
                  </Button>

                  {!hideCancel && (
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={() => setCancelDialog(true)}
                      disabled={loading}
                      startIcon={<Cancel />}
                    >
                      ✗ Cancel Order
                    </Button>
                  )}
                </Stack>
              )}

              {/* PHASE 1: PREPARING - Can upload images & mark ready + Cancel */}
              {vendorPhase.status === "PREPARING" && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, backgroundColor: "#fff3e0", borderRadius: 1, border: "1px solid #ff9800" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ⏳ Preparing Order
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      You can upload preparation images and notes. Click "Ready for Pickup" when done.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleReadyForPickup}
                    disabled={loading}
                  >
                    ✓ Ready for Pickup
                  </Button>

                  {!hideCancel && (
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={() => setCancelDialog(true)}
                      disabled={loading}
                      startIcon={<Cancel />}
                    >
                      ✗ Cancel Order
                    </Button>
                  )}
                </Stack>
              )}

              {/* PHASE 1: READY_FOR_PICKUP - Waiting for Agent + Cancel */}
              {vendorPhase.status === "READY_FOR_PICKUP" && (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ✓ Items Ready for Pickup
                    </Typography>
                  </Box>

                  {/* Show Agent Status */}
                  {agentPhase.status === "SEARCHING" && (
                    <Box sx={{ p: 2, backgroundColor: "#fff3e0", borderRadius: 1, border: "1px solid #ff9800" }}>
                      <Typography variant="caption" color="textSecondary">
                        🔍 Agent Status
                      </Typography>
                      <Chip
                        label="Agents Searching"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  )}

                  {agentPhase.status === "ACCEPTED" && (
                    <Box sx={{ p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        ✓ Agent Assigned
                      </Typography>
                    </Box>
                  )}

                  {agentPhase.status === "NONE" && (
                    <Box sx={{ p: 2, backgroundColor: "#ffebee", borderRadius: 1 }}>
                      <Typography variant="caption" color="error">
                        ⚠️ No agents available
                      </Typography>
                    </Box>
                  )}

                  {!hideCancel && (
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={() => setCancelDialog(true)}
                      disabled={loading}
                      startIcon={<Cancel />}
                    >
                      ✗ Cancel Order
                    </Button>
                  )}
                </Stack>
              )}

              {/* PHASE 1: REJECTED */}
              {vendorPhase.status === "REJECTED" && (
                <Box sx={{ p: 2, backgroundColor: "#ffebee", borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    ✗ Order Rejected
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {vendorPhase.rejectionReason}
                  </Typography>
                </Box>
              )}

              {/* PHASE 1: CANCELLED */}
              {vendorPhase.status === "CANCELLED" && (
                <Box sx={{ p: 2, backgroundColor: "#ffebee", borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    ✗ Order Cancelled
                  </Typography>
                </Box>
              )}

              {/* Divider */}
              <Box sx={{ my: 3, borderTop: "1px solid #e0e0e0" }} />

              {/* Show Agent & Delivery Status */}
              {agentPhase.status !== "NONE" && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: "block" }}>
                    🟠 Agent Phase
                  </Typography>
                  <Chip
                    label={agentPhase.status}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {deliveryPhase.status !== "NONE" && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: "block" }}>
                    🟡 Delivery Phase
                  </Typography>
                  <Chip
                    label={deliveryPhase.status}
                    size="small"
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Unified Cancel Order Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)}>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Are you sure you want to cancel this order? This action cannot be undone.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason (optional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Please provide a reason for cancelling this order"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)} disabled={loading}>
            Keep Order
          </Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? "Cancelling..." : "Yes, Cancel Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorOrderDetails;